import { useCallback, useMemo, useState } from 'react'

import { useGetProjects } from '@/hooks/queries/use-get-projects'
import { useGetRegions } from '@/hooks/queries/use-get-regions'
import { useAuth } from '@/hooks/use-auth'
import { useCompanies } from '@/hooks/use-companies'
import { Project } from '@/types/regions'

import {
  getLastSelection,
  MapsSelection,
  saveSelection,
} from '../utils/maps-selections-storage'

export function useAutoSelectProjectRegion() {
  const { user } = useAuth()
  const { selectedCompany } = useCompanies()
  const lastSelection = getLastSelection(user.id, selectedCompany.id)

  const [currentSelection, setCurrentSelection] =
    useState<MapsSelection | null>(lastSelection)

  const { data: projects = [] } = useGetProjects()

  const currentProjectId = currentSelection?.project_id || projects.at(0)?.id

  const { data: regions = [] } = useGetRegions(currentProjectId, {
    enabled: !!currentProjectId,
  })

  const currentRegionId = currentSelection?.region_id || regions.at(0)?.id

  const setSelection = useCallback(
    (project_id: number, region_id: number) => {
      const newSelection = { project_id, region_id }
      setCurrentSelection(newSelection)
      saveSelection(newSelection, user.id, selectedCompany.id)
    },
    [user.id, selectedCompany.id],
  )

  const selectedProject: Project | undefined = useMemo(() => {
    return projects.find((project) => project.id === currentProjectId)
  }, [projects, currentProjectId])

  const selectedRegion = useMemo(() => {
    return regions.find((region) => region.id === currentRegionId)
  }, [regions, currentRegionId])

  return {
    selectedProject,
    selectedRegion,
    setSelection,
  }
}
